import styled, { css } from "styled-components";

import { RankingLoadingSkeletonContainer } from "pages/[tenant]/feed/components/molecules/Ranking/LoadingSkeleton/styles";
import { ImersiveLayoutBackgroundBannerContainer } from "pages/[tenant]/g/[group_slug]/classwork/[topic_slug]/[activitie_slug]/components/atoms/ImersiveLayoutBackgroundBanner/styles";
import { GroupHorizontalLogoContainer } from "pages/[tenant]/g/[group_slug]/classwork/[topic_slug]/[activitie_slug]/components/templates/ImersiveLessonPage/components/atoms/GroupHorizontalLogo/styles";
import { GroupInfoContainer } from "pages/[tenant]/g/[group_slug]/classwork/[topic_slug]/[activitie_slug]/components/templates/ImersiveLessonPage/components/atoms/GroupInfo/styles";
import { GroupSidebarTopicsContainer } from "pages/[tenant]/g/[group_slug]/classwork/[topic_slug]/[activitie_slug]/components/templates/ImersiveLessonPage/components/molecules/GroupSidebarTopics/styles";
import { MiniRankingContainer } from "pages/[tenant]/g/[group_slug]/community/components/molecules/MiniRanking/styles";
import { MyGroupsDropdownContainer } from "pages/[tenant]/g/[group_slug]/community/components/molecules/MyGroupsDropdown/styles";
import {
  CreateCommentEditorFeedContainer,
  LexicalEditorContainer,
} from "pages/[tenant]/g/components/Feed/components/CreateCommentEditorFeed/styles";
import { CreatePostEditorContainer } from "pages/[tenant]/g/components/Feed/components/CreatePostEditorFeed/styles";
import {
  ClassworkDropdownContent,
  ClassworkDropdownTitle,
} from "pages/[tenant]/g/components/molecules/ClassworkDropdown/styles";

import { SimpleUserPhotoContainer } from "shared/components/atoms/SimpleUserPhoto/styles";
import { LoadingSkeletonBaseStyles } from "shared/components/molecules/LoadingSkeleton/atoms/styles";

export const GroupCommunityPageTemplateContainer = styled.div`
  display: grid;
  grid-template-areas: "content";
  align-items: start;

  height: 100%;

  background-color: ${({ theme }) =>
    theme.mode === "dark" ? "black" : "#F6F6FC"};

  ${ImersiveLayoutBackgroundBannerContainer} {
    grid-area: content;
  }
`;

export const LeftContent = styled.div`
  grid-area: left-content;

  position: sticky;
  top: 9rem;

  height: fit-content;

  :has(${GroupHorizontalLogoContainer} ~ ${MyGroupsDropdownContainer}) {
    ${GroupHorizontalLogoContainer} img {
      border-radius: 4px 4px 0px 0px;
    }

    ${MyGroupsDropdownContainer} {
      border-radius: 0px 0px 4px 4px;
    }
  }

  ${RankingLoadingSkeletonContainer} .user {
    overflow: hidden;
  }
`;

export const CenterContent = styled.div`
  grid-area: center-content;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;

  > ${LoadingSkeletonBaseStyles} {
    z-index: 1;
  }

  ${CreatePostEditorContainer} {
    background-color: ${({ theme }) =>
      theme.mode === "dark" ? "#131214" : "#FFFFFF"};
    box-shadow: inset 0px 0px 0px 1px
      ${({ theme }) => theme.classwork.imersive.tagBackgroundColor};

    .create-post-button {
      background-color: #1d1e22;
    }

    ${SimpleUserPhotoContainer} {
      display: none;
    }
  }

  ${CreateCommentEditorFeedContainer} ${LexicalEditorContainer} {
    background-color: ${({ theme }) =>
      theme.mode === "dark" ? "#131214" : "#FFFFFF"};
  }

  > .loader {
    width: 100%;

    margin-top: 1.5rem;
  }
`;

export const RightContent = styled.div`
  grid-area: right-content;

  position: sticky;
  top: 9rem;

  height: fit-content;

  ${GroupSidebarTopicsContainer} {
    max-height: calc(110vh - 25rem);
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 4px;
      display: block;
    }

    &::-webkit-scrollbar-track {
      width: 4px;
      display: block;
    }

    &::-webkit-scrollbar-thumb {
      width: 4px;
      display: block;
    }

    margin: 0;

    .title {
      word-break: break-word;
    }
  }

  ${ClassworkDropdownContent} {
    grid-template-columns: 1fr;

    gap: 0;
    padding: 0 1rem;

    .empty-message {
      text-align: center;
    }
  }

  ${ClassworkDropdownTitle} {
    padding: 1.375rem 1rem;

    justify-content: space-between;

    height: 64px;
    width: 100%;
  }

  ${GroupInfoContainer} {
    padding: 0.75rem 1rem;
    border: 1px solid
      ${({ theme }) => theme.classwork.imersive.dropdownBorderColor};
    background-color: ${({ theme }) =>
      theme.classwork.imersive.bluredBackgroundColor};
    backdrop-filter: blur(10px);

    margin-bottom: 1.5rem;

    .title {
      display: none;
    }

    .progress {
      flex-direction: row-reverse;

      .numbers {
        align-items: flex-start;
      }
    }
  }

  ${MiniRankingContainer} [data-testid="console-page-select"] {
    height: auto;
  }
`;

interface IGroupCommunityPageTemplateContentProps {
  shouldHideRightContent: boolean;
}

export const GroupCommunityPageTemplateContent = styled.div<IGroupCommunityPageTemplateContentProps>`
  max-width: 1530px;
  margin: 10rem auto 0 auto;

  padding: 0 1rem;

  display: grid;
  grid-template-columns: minmax(0, 304px) minmax(0, 881px) minmax(0, 350px);
  grid-template-areas: "left-content center-content right-content";
  gap: 2rem;

  grid-area: content;

  @media (max-width: 1400px) {
    margin-top: 8.75rem;
    max-width: 1300px;
    gap: 1.5rem;

    padding: 0 3rem;

    grid-template-columns: minmax(0, 220px) minmax(0, 543px) minmax(0, 300px);
  }

  @media (max-width: 1100px) {
    padding: 0 1rem;
    width: 100%;

    grid-template-columns: minmax(0, 220px) 1fr;
    grid-template-areas: "left-content center-content";

    ${RightContent} {
      display: none;
    }
  }

  @media (max-width: 990px) {
    margin-top: 4.75rem;

    ${LeftContent} {
      top: 4rem;
    }
  }

  @media (max-width: 700px) {
    padding: 0 1rem;

    grid-template-columns: 1fr;
    grid-template-areas: "center-content";

    ${LeftContent} {
      display: none;
    }
  }
`;
